import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.preprocess((v) => (v === '' ? undefined : v), z.string().email('Enter a valid email').optional()),
  phone: z.string().max(20).optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
  gender: z.string().max(50).optional().or(z.literal('')),
  addressLine1: z.string().max(255).optional().or(z.literal('')),
  addressLine2: z.string().max(255).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(2).optional().or(z.literal('')),
  zip: z.string().max(10).optional().or(z.literal('')),
  emergencyContactName: z.string().max(200).optional().or(z.literal('')),
  emergencyContactPhone: z.string().max(20).optional().or(z.literal('')),
  emergencyContactRelationship: z.string().max(100).optional().or(z.literal('')),
  smsRemindersOptedIn: z.boolean().optional(),
  notes: z.string().optional().or(z.literal('')),
});

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

export default function ClientForm({ defaultValues = {}, onSubmit, onCancel, isSubmitting }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Info */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name *" error={errors.firstName}>
            <input type="text" className="input" {...register('firstName')} />
          </Field>
          <Field label="Last name *" error={errors.lastName}>
            <input type="text" className="input" {...register('lastName')} />
          </Field>
          <Field label="Email" error={errors.email}>
            <input type="email" className="input" {...register('email')} />
          </Field>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Phone</label>
            <input type="tel" className="input" placeholder="(555) 000-0000" {...register('phone')} />
            <label className="mt-1.5 flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...register('smsRemindersOptedIn')} />
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Opt in to SMS appointment reminders</span>
            </label>
          </div>
          <Field label="Date of birth" error={errors.dateOfBirth}>
            <input type="date" className="input" {...register('dateOfBirth')} />
          </Field>
          <Field label="Gender" error={errors.gender}>
            <input type="text" className="input" placeholder="e.g. Female, Male, Non-binary" {...register('gender')} />
          </Field>
        </div>
      </div>

      {/* Address */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
          Address
        </h3>
        <div className="space-y-3">
          <Field label="Address line 1" error={errors.addressLine1}>
            <input type="text" className="input" {...register('addressLine1')} />
          </Field>
          <Field label="Address line 2" error={errors.addressLine2}>
            <input type="text" className="input" {...register('addressLine2')} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="City" error={errors.city}>
              <input type="text" className="input" {...register('city')} />
            </Field>
            <Field label="State" error={errors.state}>
              <input type="text" className="input" placeholder="CA" maxLength={2} {...register('state')} />
            </Field>
            <Field label="ZIP" error={errors.zip}>
              <input type="text" className="input" {...register('zip')} />
            </Field>
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
          Emergency Contact
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Name" error={errors.emergencyContactName}>
            <input type="text" className="input" {...register('emergencyContactName')} />
          </Field>
          <Field label="Phone" error={errors.emergencyContactPhone}>
            <input type="tel" className="input" {...register('emergencyContactPhone')} />
          </Field>
          <Field label="Relationship" error={errors.emergencyContactRelationship}>
            <input type="text" className="input" placeholder="e.g. Spouse" {...register('emergencyContactRelationship')} />
          </Field>
        </div>
      </div>

      {/* Notes */}
      <Field label="Notes" error={errors.notes}>
        <textarea
          className="input"
          rows={3}
          placeholder="Internal notes about this client..."
          {...register('notes')}
        />
      </Field>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        )}
        <button type="submit" disabled={isSubmitting} className="btn btn-primary disabled:opacity-60">
          {isSubmitting ? 'Saving…' : 'Save client'}
        </button>
      </div>
    </form>
  );
}
